import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { clsx } from "clsx";
import {
	ChevronDown,
	ChevronRight,
	GripVertical,
	MoreHorizontal,
	Pencil,
	Plus,
	Ruler,
	Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";

import type { StageKind } from "@/adapters/api";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { HistoryDialog } from "@/features/measurements/components/history-dialog";
import { MeasureButton } from "@/features/measurements/components/measure-button";
import { MeasuredCell } from "@/features/measurements/components/measured-cell";
import { MeasurementCell } from "@/features/measurements/components/measurement-cell";
import { MeasurementDialog } from "@/features/measurements/components/measurement-dialog";
import {
	useLatestMeasurements,
	useLatestRollups,
} from "@/features/measurements/hooks/use-latest-measurements.ts";
import type {
	HistoryTarget,
	Measurement,
	MeasurementTarget,
	Rollup,
} from "@/features/measurements/types.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { formatBRL } from "@/lib/currency.ts";
import { useDeleteStage } from "../../hooks/use-delete-stage.ts";
import { useReorderStage } from "../../hooks/use-reorder-stage.ts";
import type { Stage } from "../../types.ts";
import { STAGE_KIND } from "../../utils/kind.ts";
import { isStagesLockedError } from "../../utils/lock.ts";
import {
	childrenCount,
	descendantIds,
	effectivelyCancelledIds,
	moveSiblingBlock,
	visibleStages,
} from "../../utils/tree.ts";
import { StatusBadge } from "../status-badge";
import { StatusMenu } from "../status-menu";

function deleteDescription(stage: Stage, descendants: number): string {
	if (descendants === 0) {
		return `Tem certeza que deseja remover a etapa “${stage.name}”? Esta ação não pode ser desfeita.`;
	}
	const plural = descendants === 1 ? "sub-etapa" : "sub-etapas";
	return `Esta ação removerá também ${descendants} ${plural} de “${stage.name}” e não pode ser desfeita.`;
}

export function StagesTableSkeleton() {
	return (
		<Card className="stages-table__card">
			<div className="stages-table__skeleton">
				{Array.from({ length: 4 }, (_, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list
					<Skeleton key={index} className="stages-table__skeleton-row" />
				))}
			</div>
		</Card>
	);
}

interface RowActions {
	constructionId: string;
	canCreate: boolean;
	canUpdate: boolean;
	canDelete: boolean;
	canChangeStatus: boolean;
	canMeasure: boolean;
	showMeasurement: boolean;
	showValue: boolean;
	showMeasuredMoney: boolean;
	stagePath: (stageId: string) => string;
	newStagePath: (parentId?: string, kind?: StageKind) => string;
	onDelete: (stage: Stage) => void;
	onMeasure: (stage: Stage) => void;
	onOpenHistory: (stage: Stage, isLeaf: boolean) => void;
	onToggleCollapsed: (id: string) => void;
}

function SortableStageRow({
	stage,
	childCount,
	isCollapsed,
	isCancelled,
	measurement,
	rollup,
	actions,
}: {
	stage: Stage;
	childCount: number;
	isCollapsed: boolean;
	isCancelled: boolean;
	measurement: Measurement | undefined;
	rollup: Rollup | undefined;
	actions: RowActions;
}) {
	const {
		constructionId,
		canCreate,
		canUpdate,
		canDelete,
		canChangeStatus,
		canMeasure,
		showMeasurement,
		showValue,
		showMeasuredMoney,
		stagePath,
		newStagePath,
		onDelete,
		onMeasure,
		onOpenHistory,
		onToggleCollapsed,
	} = actions;
	const isLeaf = childCount === 0;
	const canAddChild = canCreate && stage.kind !== "service";
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: stage.id, disabled: !canUpdate });

	return (
		<TableRow
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			className={clsx("stages-table__row", isDragging && "is-dragging")}
		>
			<TableCell className="stages-table__item-col">
				{stage.itemNumber}
			</TableCell>
			<TableCell className="stages-table__name-cell">
				<div
					className="stages-table__name"
					style={{ paddingLeft: `${stage.depth * 1.5}rem` }}
				>
					{canUpdate && (
						<button
							type="button"
							ref={setActivatorNodeRef}
							{...attributes}
							{...listeners}
							aria-label={`Reordenar ${stage.name}`}
							className="stages-table__grip"
						>
							<GripVertical />
						</button>
					)}
					{childCount > 0 ? (
						<Button
							variant="ghost"
							size="icon"
							className="stages-table__toggle"
							onClick={() => onToggleCollapsed(stage.id)}
							aria-label={
								isCollapsed
									? `Expandir ${stage.name}`
									: `Recolher ${stage.name}`
							}
						>
							{isCollapsed ? <ChevronRight /> : <ChevronDown />}
						</Button>
					) : (
						<span className="stages-table__spacer" aria-hidden />
					)}
					<div className="stages-table__info">
						<div className="stages-table__name-line">
							<p className="stages-table__item-name">{stage.name}</p>
							<Badge
								variant="outline"
								className="kind-badge"
								data-kind={stage.kind}
							>
								{STAGE_KIND[stage.kind].label}
							</Badge>
						</div>
						{stage.description && (
							<p className="stages-table__item-desc">{stage.description}</p>
						)}
					</div>
				</div>
			</TableCell>
			{showValue && (
				<TableCell
					className={clsx(
						"stages-table__value-col",
						!isLeaf && "is-branch",
						isCancelled && "is-cancelled",
					)}
				>
					{isCancelled || stage.valueCents === undefined
						? "—"
						: formatBRL(stage.valueCents)}
				</TableCell>
			)}
			{showMeasurement && (
				<TableCell className="stages-table__measurement-col">
					<MeasurementCell
						measurement={measurement}
						rollup={rollup}
						isLeaf={isLeaf}
						cancelled={isCancelled}
					/>
				</TableCell>
			)}
			{showMeasurement && (
				<TableCell className="stages-table__measured-col">
					<MeasuredCell
						stageName={stage.name}
						isLeaf={isLeaf}
						measurement={measurement}
						rollup={rollup}
						cancelled={isCancelled}
						showMoney={showMeasuredMoney}
						onOpenHistory={() => onOpenHistory(stage, isLeaf)}
					/>
				</TableCell>
			)}
			<TableCell className="stages-table__status-col">
				{canChangeStatus ? (
					<StatusMenu constructionId={constructionId} stage={stage} />
				) : (
					<StatusBadge status={stage.status} />
				)}
			</TableCell>
			<TableCell className="stages-table__actions-cell">
				<div className="stages-table__actions">
					{canMeasure && isLeaf && (
						<MeasureButton
							className="stages-table__action"
							stageName={stage.name}
							onClick={() => onMeasure(stage)}
						/>
					)}
					{canAddChild && (
						<Button
							asChild
							variant="ghost"
							size="icon"
							className="stages-table__action"
						>
							<Link
								to={newStagePath(stage.id)}
								aria-label={`Adicionar subetapa em ${stage.name}`}
							>
								<Plus />
							</Link>
						</Button>
					)}
					{canUpdate && (
						<Button
							asChild
							variant="ghost"
							size="icon"
							className="stages-table__action"
						>
							<Link
								to={stagePath(stage.id)}
								aria-label={`Editar ${stage.name}`}
							>
								<Pencil />
							</Link>
						</Button>
					)}
					{canDelete && (
						<Button
							variant="ghost"
							size="icon"
							className="stages-table__action-delete"
							onClick={() => onDelete(stage)}
							aria-label={`Remover ${stage.name}`}
						>
							<Trash2 />
						</Button>
					)}
				</div>
				{(canAddChild || canUpdate || canDelete || (canMeasure && isLeaf)) && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="stages-table__action-menu"
								aria-label={`Abrir menu de ${stage.name}`}
							>
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							{canMeasure && isLeaf && (
								<DropdownMenuItem onClick={() => onMeasure(stage)}>
									<Ruler />
									Lançar medição
								</DropdownMenuItem>
							)}
							{canAddChild && (
								<DropdownMenuItem asChild>
									<Link to={newStagePath(stage.id, "substage")}>
										<Plus />
										Adicionar subetapa
									</Link>
								</DropdownMenuItem>
							)}
							{canAddChild && (
								<DropdownMenuItem asChild>
									<Link to={newStagePath(stage.id, "service")}>
										<Plus />
										Adicionar serviço
									</Link>
								</DropdownMenuItem>
							)}
							{canUpdate && (
								<DropdownMenuItem asChild>
									<Link to={stagePath(stage.id)}>
										<Pencil />
										Editar
									</Link>
								</DropdownMenuItem>
							)}
							{(canAddChild || canUpdate) && canDelete && (
								<DropdownMenuSeparator />
							)}
							{canDelete && (
								<DropdownMenuItem
									variant="destructive"
									onClick={() => onDelete(stage)}
								>
									<Trash2 />
									Remover
								</DropdownMenuItem>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</TableCell>
		</TableRow>
	);
}

export function StagesTable({
	constructionId,
	stages,
	locked,
	valued,
	stagePath,
	newStagePath,
}: {
	constructionId: string;
	stages: Stage[];
	locked: boolean;
	valued: boolean;
	stagePath: (stageId: string) => string;
	newStagePath: (parentId?: string, kind?: StageKind) => string;
}) {
	const { has } = usePermissions();
	const canCreate = has("construction_stages:create") && !locked;
	const canChangeStatus = has("construction_stages:update");
	const canUpdate = canChangeStatus && !locked;
	const canDelete = has("construction_stages:delete") && !locked;
	const canMeasure = has("stage_measurements:create");
	const showMeasurement = has("stage_measurements:list");
	const showValue = has("stage_values:view");
	const showMeasuredMoney = showValue && valued;
	const columnCount = 4 + (showMeasurement ? 2 : 0) + (showValue ? 1 : 0);

	const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
	const [stageToDelete, setStageToDelete] = useState<Stage | null>(null);
	const [stageToMeasure, setStageToMeasure] = useState<MeasurementTarget | null>(
		null,
	);
	const [historyTarget, setHistoryTarget] = useState<HistoryTarget | null>(
		null,
	);
	const deleteStage = useDeleteStage(constructionId);
	const reorderStage = useReorderStage(constructionId);
	const latestMeasurements = useLatestMeasurements(
		showMeasurement ? constructionId : undefined,
	);
	const latestRollups = useLatestRollups(
		showMeasurement ? constructionId : undefined,
	);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const counts = useMemo(() => childrenCount(stages), [stages]);
	const cancelledIds = useMemo(() => effectivelyCancelledIds(stages), [stages]);
	const rows = useMemo(
		() => visibleStages(stages, collapsed),
		[stages, collapsed],
	);
	const valueByStage = useMemo(() => {
		if (!showMeasuredMoney) return undefined;
		const values = new Map<string, number>();
		for (const stage of stages) {
			if (stage.valueCents !== undefined)
				values.set(stage.id, stage.valueCents);
		}
		return values;
	}, [stages, showMeasuredMoney]);
	const descendantsToDelete = stageToDelete
		? descendantIds(stages, stageToDelete.id).size
		: 0;

	function toggleCollapsed(id: string) {
		setCollapsed((current) => {
			const next = new Set(current);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}

	function handleConfirmDelete() {
		if (!stageToDelete) return;
		deleteStage.mutate(stageToDelete.id, {
			onSuccess: () => {
				toast.success("Etapa removida com sucesso!");
				setStageToDelete(null);
			},
			onError: (err) => {
				if (!isStagesLockedError(err)) {
					toast.error("Houve um erro ao remover a etapa.");
				}
				setStageToDelete(null);
			},
		});
	}

	function handleDragEnd(event: DragEndEvent) {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const move = moveSiblingBlock(stages, String(active.id), String(over.id));
		if (!move) {
			toast.info(
				"Arraste apenas entre etapas do mesmo nível. Para mudar o nível, use a edição ou o diagrama.",
			);
			return;
		}
		reorderStage.mutate(move, {
			onError: (err) => {
				if (!isStagesLockedError(err)) {
					toast.error("Houve um erro ao reordenar as etapas.");
				}
			},
		});
	}

	const actions: RowActions = {
		constructionId,
		canCreate,
		canUpdate,
		canDelete,
		canChangeStatus,
		canMeasure,
		showMeasurement,
		showValue,
		showMeasuredMoney,
		stagePath,
		newStagePath,
		onDelete: setStageToDelete,
		onMeasure: (stage) => setStageToMeasure({ id: stage.id, name: stage.name }),
		onOpenHistory: (stage, isLeaf) =>
			setHistoryTarget({
				id: stage.id,
				name: stage.name,
				itemNumber: stage.itemNumber,
				isLeaf,
			}),
		onToggleCollapsed: toggleCollapsed,
	};

	return (
		<>
			<Card className="stages-table__card">
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<Table>
						<TableHeader className="stages-table__table-header">
							<TableRow>
								<TableHead className="stages-table__item-col">Item</TableHead>
								<TableHead className="stages-table__head-name">Nome</TableHead>
								{showValue && (
									<TableHead className="stages-table__value-col">
										Valor
									</TableHead>
								)}
								{showMeasurement && (
									<TableHead className="stages-table__measurement-col">
										Realizado
									</TableHead>
								)}
								{showMeasurement && (
									<TableHead className="stages-table__measured-col">
										Medido
									</TableHead>
								)}
								<TableHead className="stages-table__status-col">
									Status
								</TableHead>
								<TableHead className="stages-table__actions-cell">
									Ações
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.length === 0 ? (
								<TableRow>
									<TableCell
										colSpan={columnCount}
										className="stages-table__empty"
									>
										Nenhuma etapa cadastrada.
									</TableCell>
								</TableRow>
							) : (
								<SortableContext
									items={rows.map((stage) => stage.id)}
									strategy={verticalListSortingStrategy}
								>
									{rows.map((stage) => (
										<SortableStageRow
											key={stage.id}
											stage={stage}
											childCount={counts.get(stage.id) ?? 0}
											isCollapsed={collapsed.has(stage.id)}
											isCancelled={cancelledIds.has(stage.id)}
											measurement={latestMeasurements.data?.get(stage.id)}
											rollup={latestRollups.data?.get(stage.id)}
											actions={actions}
										/>
									))}
								</SortableContext>
							)}
						</TableBody>
					</Table>
				</DndContext>
			</Card>

			<ConfirmDialog
				open={!!stageToDelete}
				onOpenChange={(open) => {
					if (!open) setStageToDelete(null);
				}}
				title="Remover Etapa"
				description={
					stageToDelete
						? deleteDescription(stageToDelete, descendantsToDelete)
						: ""
				}
				confirmLabel="Remover"
				destructive
				isPending={deleteStage.isPending}
				onConfirm={handleConfirmDelete}
			/>

			{showMeasurement && (
				<HistoryDialog
					open={!!historyTarget}
					onOpenChange={(open) => {
						if (!open) setHistoryTarget(null);
					}}
					constructionId={constructionId}
					target={historyTarget}
					valueByStage={valueByStage}
				/>
			)}

			{canMeasure && (
				<MeasurementDialog
					open={!!stageToMeasure}
					onOpenChange={(open) => {
						if (!open) setStageToMeasure(null);
					}}
					constructionId={constructionId}
					stage={stageToMeasure}
				/>
			)}
		</>
	);
}
